import { Auth, FieldRow, Form } from '@/components';
import { GenerateCustomReportFilterDto, ProjectStatus } from '@/core';
import { Button, Col, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  useClientLookupSource,
  useReportingCommunitiesLookupSource,
  useReportingHomesLookupSource,
} from '@/views';
import styles from './FormCustomReport.module.scss';
import { useEffect } from 'react';
import { ReportingProjectSelect } from '../Reports.HomeStatus/ReportingProjectSelect';

const { Text } = Typography;

interface Props {
  initialValue: GenerateCustomReportFilterDto;
  name: string;
  className?: string;
  clientId?: string;
  communityId?: string;
  projectId?: string;
}

export function FormCustomReportFilter(props: Props) {
  const { name, initialValue, className, communityId, clientId } = props;
  const [{ value }, meta, { setValue }] =
    useField<GenerateCustomReportFilterDto>(name);
  const { t } = useTranslation(undefined, {
    keyPrefix: 'reports.custom.details.filter',
  });
  const { clientIds, communityIds, projectIds } = value;

  const clientDataSource = useClientLookupSource();
  const communityDataSource = useReportingCommunitiesLookupSource(
    clientIds || false,
  );
  const homeDataSource = useReportingHomesLookupSource(
    clientIds,
    communityIds,
    projectIds,
  );

  const { setFieldValue } = useFormikContext();

  useEffect(() => {
    meta.touched && setFieldValue(`${name}.communityIds`, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIds, name, setFieldValue]);

  useEffect(() => {
    meta.touched && setFieldValue(`${name}.projectIds`, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityIds, name, setFieldValue]);

  useEffect(() => {
    meta.touched && setFieldValue(`${name}.homeIds`, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectIds, name, setFieldValue]);

  return (
    <>
      <div className={styles.filter}>
        <Text>{t('filterBy')}:</Text>
      </div>
      <FieldRow className={className} wrap justify="space-between">
        <Col span={12}>
          <Form.Date
            required
            label={false}
            name={name + '.startDate'}
            placeholder={t('placeholders.startDate')}
            allowClear={false}
          />
        </Col>
        <Col span={12}>
          <Form.Date
            label={false}
            required
            name={name + '.endDate'}
            placeholder={t('placeholders.endDate')}
            allowClear={false}
          />
        </Col>
        <Auth role="BFP_ADMIN">
          <Col span={12}>
            <Form.AsyncSelect
              label={false}
              dataSource={clientDataSource}
              name={name + '.clientIds'}
              placeholder={t('placeholders.clientIds')}
              mode="multiple"
              preloadInitial={clientId}
            />
          </Col>
        </Auth>
        <Col span={12}>
          <Form.AsyncSelect
            key={clientIds?.join()}
            dataSource={communityDataSource}
            label={false}
            name={name + '.communityIds'}
            placeholder={t('placeholders.communityIds')}
            mode="multiple"
            preloadInitial={communityId}
          />
        </Col>
        <Col span={12}>
          <ReportingProjectSelect
            name={name + '.projectIds'}
            communityId={communityIds}
            label={false}
            placeholder={t('placeholders.projectIds')}
            showCommunityName={false}
          />
        </Col>
        <Col span={12}>
          <Form.EnumSelect
            name={name + '.projectStatuses'}
            label={false}
            type={ProjectStatus}
            mode="multiple"
            placeholder={t('placeholders.projectStatuses')}
          />
        </Col>
        <Col span={12}>
          <Form.AsyncSelect
            label={false}
            key={projectIds?.concat(communityIds ?? [])?.join()}
            dataSource={homeDataSource}
            name={name + '.homeIds'}
            placeholder={t('placeholders.homeIds')}
            mode="multiple"
          />
        </Col>
      </FieldRow>
      <Button
        className={styles.clearButton}
        icon={<CloseOutlined />}
        onClick={() => setValue(initialValue)}
      >
        {t('clearAll')}
      </Button>
    </>
  );
}
